import { action, noop, withAssign } from '@reatom/framework'
import i18next from 'i18next'

import { AsyncStateN, Result } from '@blade/std-lib'
import { userReceivedAction } from '@root/entities/auth'
import { ApiError } from '@root/shared/api'
import { AuthErrorCode } from '@root/shared/api/api-error-code'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import { WrongOtpError } from '../../auth-errors'
import { openOtpModalEvent } from '../../authenticate'

import {
  confirmOtpRequestAction,
  createOtpRequestAction,
  deleteOtpRequestAction,
} from './otp-requests'

export const reatomConfirmOtp = () => {
  const confirmStateAtom = reatomRequest({
    execAction: confirmOtpRequestAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'setupOtpReatom',
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        userReceivedAction(ctx, result.success)
        showSnackbarAction(ctx, {
          text: i18next.t('mfa.mfa_successfully_set_message'),
          intent: 'success',
        })
      } else {
        if (ApiError.isOfCodes([AuthErrorCode.InvalidOTP])(result.failure)) {
          showSnackbarAction(ctx, {
            text: i18next.t('otp.modal.error'),
            intent: 'error',
          })
          return
        }

        showErrorSnackbarAction(ctx, result.failure)
      }
    },
  })

  return confirmStateAtom
}
export type SetupOtpReatom = ReturnType<typeof reatomConfirmOtp>

export const reatomCreateOtp = () => {
  const {
    executeAction: createOtpAction,
    requestStateAtom: createOtpRequestState,
  } = reatomRequest({
    execAction: createOtpRequestAction,
    name: 'reatomCreateOtp',
  })

  return {
    createOtpAction,
    createOtpRequestState,
  }
}
export type CreateOtpReatom = ReturnType<typeof reatomCreateOtp>

// @deprecated - use settingScreenAction instead of this event
export const startMfaSetupEvent = action(noop, 'startMfaSetupEvent')

export const reatomDisableMfa = () => {
  const disableMfaStateAtom = reatomRequest({
    execAction: deleteOtpRequestAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'reatomDisableMfa',
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        userReceivedAction(ctx, result.success)
        showSnackbarAction(ctx, {
          text: i18next.t('mfa.mfa-removed.message'),
          intent: 'success',
        })
      }
    },
  })

  const disable = action((ctx) => {
    const controller = openOtpModalEvent(ctx, {
      onOtp: async (ctx, { code }) => {
        controller.setState(ctx, AsyncStateN.loading())
        const result = await disableMfaStateAtom.execute(ctx, { code })

        if (Result.isSuccess(result)) {
          controller.setState(ctx, AsyncStateN.success(undefined))
          controller.closeAction(ctx)
          return
        }

        if (ApiError.isOfCodes([AuthErrorCode.InvalidOTP])(result.failure)) {
          controller.setState(ctx, AsyncStateN.error(new WrongOtpError()))
          return
        }

        showErrorSnackbarAction(ctx, result.failure)
        controller.closeAction(ctx)
      },
    })
  }, 'disable')

  return disableMfaStateAtom.pipe(
    withAssign(() => ({
      disable: disable,
    })),
  )
}

export const reatomMfaSetup = () => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const createOtp = reatomCreateOtp()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const confirmOtp = reatomConfirmOtp()

  return {
    createOtp,
    confirmOtp,
  }
}

export type MfaSetupModel = ReturnType<typeof reatomMfaSetup>
